import { UserMessageAttachments } from "@/components/assistant-ui/attachment";
import { defaultComponents } from "@/components/assistant-ui/markdown-text";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import { SystemNotice } from "@/components/assistant-ui/system-notice";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { BrainstormIndicator } from "@/components/project/BrainstormIndicator";
import { type AgentMode } from "@/components/project/ModeSelector";
import { QuickReplyChips } from "@/components/project/QuickReplyChips";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { parseAgentMarkers } from "@/lib/utils/parse-markers";
import {
  ActionBarMorePrimitive,
  ActionBarPrimitive,
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  SuggestionPrimitive,
  ThreadPrimitive,
  useMessage,
  useThreadRuntime,
} from "@assistant-ui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  ImageIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SquareIcon,
  XIcon,
} from "lucide-react";
import type { FC } from "react";

interface ThreadProps {
  selectedAttachmentName?: string | null;
  selectedAttachmentSizeBytes?: number | null;
  selectedAttachmentPreviewUrl?: string | null;
  onOpenAttachmentPicker?: () => void;
  onClearAttachment?: () => void;
  mode?: AgentMode;
  onModeChange?: (mode: AgentMode) => void;
}

export const Thread: FC<ThreadProps> = ({
  selectedAttachmentName,
  selectedAttachmentSizeBytes,
  selectedAttachmentPreviewUrl,
  onOpenAttachmentPicker,
  onClearAttachment,
  mode,
  onModeChange,
}) => {
  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
      style={{
        ["--thread-max-width" as string]: "44rem",
      }}
    >
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        autoScroll={true}
        className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth px-2.5 pt-0"
        // className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth px-4 pr-1 pt-4"
      >
        <AuiIf condition={(s) => s.thread.isEmpty}>
          <ThreadWelcome />
        </AuiIf>

        <ThreadPrimitive.Messages
          components={{
            UserMessage,
            EditComposer,
            AssistantMessage,
          }}
        />

        <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-(--thread-max-width) flex-col gap-4 overflow-visible rounded-t-3xl bg-background pb-2">
          {/* Gradient fader — visually fades messages into the composer */}
          <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-t from-background to-transparent" />
          <ThreadScrollToBottom />
          <Composer
            selectedAttachmentName={selectedAttachmentName}
            selectedAttachmentSizeBytes={selectedAttachmentSizeBytes}
            selectedAttachmentPreviewUrl={selectedAttachmentPreviewUrl}
            onOpenAttachmentPicker={onOpenAttachmentPicker}
            onClearAttachment={onClearAttachment}
            mode={mode}
            onModeChange={onModeChange}
          />
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="aui-thread-scroll-to-bottom absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible dark:bg-background dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="aui-thread-welcome-center flex w-full grow flex-col items-center justify-center">
        <div className="aui-thread-welcome-message flex size-full flex-col justify-center px-4">
          <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in font-semibold text-2xl duration-200">
            Hello there!
          </h1>
          <p className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in text-muted-foreground text-xl delay-75 duration-200">
            How can I help you today?
          </p>
        </div>
      </div>
      <ThreadSuggestions />
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  return (
    <div className="aui-thread-welcome-suggestions grid w-full @md:grid-cols-2 gap-2 pb-4">
      <ThreadPrimitive.Suggestions
        components={{
          Suggestion: ThreadSuggestionItem,
        }}
      />
    </div>
  );
};

const ThreadSuggestionItem: FC = () => {
  return (
    <div className="aui-thread-welcome-suggestion-display fade-in slide-in-from-bottom-2 @md:nth-[n+3]:block nth-[n+3]:hidden animate-in fill-mode-both duration-200">
      <SuggestionPrimitive.Trigger send asChild>
        <Button
          variant="ghost"
          className="aui-thread-welcome-suggestion h-auto w-full @md:flex-col flex-wrap items-start justify-start gap-1 rounded-2xl border px-4 py-3 text-left text-sm transition-colors hover:bg-muted"
        >
          <span className="aui-thread-welcome-suggestion-text-1 font-medium">
            <SuggestionPrimitive.Title />
          </span>
          <span className="aui-thread-welcome-suggestion-text-2 text-muted-foreground">
            <SuggestionPrimitive.Description />
          </span>
        </Button>
      </SuggestionPrimitive.Trigger>
    </div>
  );
};

const Composer: FC<ThreadProps> = ({
  selectedAttachmentName,
  selectedAttachmentSizeBytes,
  selectedAttachmentPreviewUrl,
  onOpenAttachmentPicker,
  onClearAttachment,
  mode,
  onModeChange,
}) => {
  return (
    <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
      <ComposerPrimitive.AttachmentDropzone className="aui-composer-attachment-dropzone flex w-full flex-col rounded-2xl border border-input bg-background px-1 pt-2 outline-none transition-shadow data-[dragging=true]:border-ring data-[dragging=true]:border-dashed data-[dragging=true]:bg-accent/50">
        {selectedAttachmentName && (
          <ComposerAttachmentChip
            name={selectedAttachmentName}
            sizeBytes={selectedAttachmentSizeBytes}
            previewUrl={selectedAttachmentPreviewUrl}
            onClear={onClearAttachment}
          />
        )}
        <ComposerPrimitive.Input
          placeholder="Ask Rayloop..."
          className="aui-composer-input mb-1 max-h-32 min-h-14 w-full resize-none bg-transparent px-4 pt-2 pb-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0"
          rows={1}
          autoFocus
          aria-label="Message input"
        />
        <ComposerAction
          onOpenAttachmentPicker={onOpenAttachmentPicker}
          mode={mode}
          onModeChange={onModeChange}
        />
      </ComposerPrimitive.AttachmentDropzone>
    </ComposerPrimitive.Root>
  );
};

const ComposerAttachmentChip: FC<{
  name: string;
  sizeBytes?: number | null;
  previewUrl?: string | null;
  onClear?: () => void;
}> = ({ name, sizeBytes, previewUrl, onClear }) => {
  const sizeLabel = (() => {
    if (!sizeBytes || sizeBytes <= 0) return "Image";
    const kb = 1024;
    const mb = kb * 1024;
    if (sizeBytes >= mb) return `${(sizeBytes / mb).toFixed(1)} MB`;
    if (sizeBytes >= kb) return `${(sizeBytes / kb).toFixed(1)} KB`;
    return `${sizeBytes} B`;
  })();

  return (
    <div className="mx-1 mb-1 flex w-fit max-w-[280px] items-center gap-2 rounded-lg border border-border/60 bg-muted/50 px-1.5 py-1.5">
      <div className="flex size-10 items-center justify-center overflow-hidden rounded-lg bg-muted/60">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="size-full object-cover" />
        ) : (
          <ImageIcon className="size-4 text-muted-foreground/60" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-foreground/85 leading-tight">
          {name}
        </p>
        <p className="text-xs text-muted-foreground/80 leading-tight mt-0.5">
          {sizeLabel}
        </p>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="flex size-6 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive"
        aria-label="Remove selected image"
      >
        <XIcon className="size-3.5" />
      </button>
    </div>
  );
};

const MODE_OPTIONS: { value: AgentMode; label: string; description: string }[] =
  [
    { value: "auto-detect", label: "Auto", description: "Let Rayloop decide" },
    { value: "auto", label: "Fast", description: "Build immediately" },
    { value: "plan", label: "Plan", description: "Review a plan first" },
    {
      value: "brainstorm",
      label: "Brainstorm",
      description: "Explore ideas first",
    },
  ];

const ComposerAction: FC<{
  onOpenAttachmentPicker?: () => void;
  mode?: AgentMode;
  onModeChange?: (mode: AgentMode) => void;
}> = ({ onOpenAttachmentPicker, mode, onModeChange }) => {
  const currentModeLabel =
    MODE_OPTIONS.find((m) => m.value === mode)?.label ?? "Mode";
  const isNonDefault = mode !== undefined && mode !== "auto-detect";

  return (
    <div className="aui-composer-action-wrapper relative mx-2 mb-2 flex items-center justify-between">
      <div className="flex items-center gap-0.5">
        <TooltipIconButton
          tooltip="Add image from assets"
          side="bottom"
          variant="ghost"
          size="icon"
          className="size-8.5 rounded-full p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30"
          aria-label="Add image from assets"
          onClick={onOpenAttachmentPicker}
        >
          <PlusIcon className="size-5 stroke-[1.5px]" />
        </TooltipIconButton>

        {mode !== undefined && onModeChange && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                  isNonDefault
                    ? "bg-[oklch(0.55_0.25_285_/_0.12)] text-[oklch(0.55_0.25_285)] hover:bg-[oklch(0.55_0.25_285_/_0.18)]"
                    : "text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground",
                )}
                aria-label="Select mode"
              >
                {currentModeLabel}
                <ChevronDownIcon className="size-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-1.5" align="start" side="top">
              {MODE_OPTIONS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => onModeChange(m.value)}
                  className="w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-accent cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        "text-sm font-medium",
                        mode === m.value
                          ? "text-[oklch(0.55_0.25_285)]"
                          : "text-foreground",
                      )}
                    >
                      {m.label}
                    </span>
                    {mode === m.value && (
                      <CheckIcon className="size-3.5 text-[oklch(0.55_0.25_285)]" />
                    )}
                  </div>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {m.description}
                  </p>
                </button>
              ))}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip="Send message"
            side="bottom"
            type="submit"
            variant="default"
            size="icon"
            className="aui-composer-send size-8 rounded-full"
            aria-label="Send message"
          >
            <ArrowUpIcon className="aui-composer-send-icon size-4" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </AuiIf>
      <AuiIf condition={(s) => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel size-8 rounded-full"
            aria-label="Stop generating"
          >
            <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
          </Button>
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

/**
 * AgentText — wraps assistant text parts to parse and render embedded markers.
 * Strips <!--brainstorm:{...}--> and <!--chips:[...]--> from visible text,
 * then renders BrainstormIndicator and QuickReplyChips as sibling UI.
 */
const AgentText: FC = () => {
  const message = useMessage();
  const threadRuntime = useThreadRuntime();

  // Gather all text from this message part into one string
  const rawText = message.content
    .filter((p) => p.type === "text")
    .map((p) => (p as { type: "text"; text: string }).text)
    .join("");

  const { text, brainstormPhase, chips } = parseAgentMarkers(rawText);

  const handleChipSelect = (chip: string) => {
    threadRuntime.composer.setText(chip);
    threadRuntime.composer.send();
  };

  // Only show chips on the last assistant message — older chips are stale
  const showChips = message.isLast && chips.length > 0;

  return (
    <div>
      {brainstormPhase && <BrainstormIndicator phase={brainstormPhase} />}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={defaultComponents as any}
      >
        {text}
      </ReactMarkdown>
      {showChips && (
        <QuickReplyChips chips={chips} onSelect={handleChipSelect} />
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const assistantMessageComponents = {
  Text: AgentText,
  tools: {
    Fallback: ToolFallback,
  },
  "system-notice": SystemNotice,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

const AssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
    >
      <div className="aui-assistant-message-content wrap-break-word px-2 text-foreground leading-relaxed">
        <MessagePrimitive.Parts components={assistantMessageComponents} />
        <MessageError />
      </div>

      <div className="aui-assistant-message-footer mt-1 ml-2 flex">
        <BranchPicker />
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      autohideFloat="single-branch"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground data-floating:absolute data-floating:rounded-md data-floating:border data-floating:bg-background data-floating:p-1 data-floating:shadow-sm"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy">
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="Refresh">
          <RefreshCwIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
      <ActionBarMorePrimitive.Root>
        <ActionBarMorePrimitive.Trigger asChild>
          <TooltipIconButton
            tooltip="More"
            className="data-[state=open]:bg-accent"
          >
            <MoreHorizontalIcon />
          </TooltipIconButton>
        </ActionBarMorePrimitive.Trigger>
        <ActionBarMorePrimitive.Content
          side="bottom"
          align="start"
          className="aui-action-bar-more-content z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <ActionBarPrimitive.ExportMarkdown asChild>
            <ActionBarMorePrimitive.Item className="aui-action-bar-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
              <DownloadIcon className="size-4" />
              Export as Markdown
            </ActionBarMorePrimitive.Item>
          </ActionBarPrimitive.ExportMarkdown>
        </ActionBarMorePrimitive.Content>
      </ActionBarMorePrimitive.Root>
    </ActionBarPrimitive.Root>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-1 px-2 py-3 duration-150 [&:where(>*)]:col-start-2 [&:has(.aui-user-message-attachments-end:not(:empty))_.aui-user-message-content]:rounded-br-none"
      data-role="user"
    >
      <div className="aui-user-message-content-wrapper relative col-start-2 row-start-1 min-w-0">
        <div className="aui-user-message-content wrap-break-word rounded-2xl bg-muted dark:bg-white/10 px-4 py-2.5 text-foreground">
          <MessagePrimitive.Parts />
        </div>
        <div className="aui-user-action-bar-wrapper absolute top-1/2 left-0 -translate-x-full -translate-y-1/2 pr-2">
          <UserActionBar />
        </div>
      </div>

      <UserMessageAttachments />

      <BranchPicker className="aui-user-branch-picker col-span-full col-start-1 row-start-3 -mr-1 mt-2 justify-end" />
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip="Edit" className="aui-user-action-edit p-4">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
      <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none"
          autoFocus
        />
        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm">Update</Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-muted-foreground text-xs",
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="Previous">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="Next">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
